import { type ChecklistDef, type ProtocolTemplateDto, type WorkDto, datePl } from '@bqcrm/shared';
import { ActionIcon, Button, Card, Checkbox, Group, NumberInput, Select, Stack, Switch, Table, Text, Textarea, TextInput } from '@mantine/core';
import { IconArrowDown, IconArrowUp, IconPlus, IconTrash } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../../api';
import { ask } from '../../confirm';
import { formatDateTime } from '../../format';
import { useSaveShortcut } from '../../hooks';
import { notifyError, notifyOk } from '../../notify';

const move = <T,>(list: T[], i: number, d: -1 | 1): T[] => {
  const j = i + d;
  if (j < 0 || j >= list.length) return list;
  const out = [...list];
  [out[i], out[j]] = [out[j]!, out[i]!];
  return out;
};

// ── lista czynności i minimum zdjęć ──────────────────────────────────────────

/**
 * Edytor listy czynności: realizacji branży albo ogólnej rodzaju zadania. `isNew` — branża korzysta
 * z listy ogólnej (pokazanej jako punkt wyjścia); zapis tworzy jej własną listę.
 */
export function WorkEditor({ work, isNew, onSaved }: { work: WorkDto; isNew?: boolean; onSaved: (l: WorkDto[]) => void }) {
  const [items, setItems] = useState<ChecklistDef[]>(work.items);
  const [minPhotos, setMinPhotos] = useState(work.minPhotos);
  useEffect(() => {
    setItems(work.items);
    setMinPhotos(work.minPhotos);
  }, [work]);
  const url = `/settings/works/${encodeURIComponent(work.tradeCode)}/${work.taskType}`;
  const general = work.tradeCode === '*';
  const save = useMutation({
    mutationFn: () => api<WorkDto[]>(url, { method: 'PUT', body: { items, minPhotos } }),
    onSuccess: (l) => {
      notifyOk('Zapisano czynności — dotyczy nowych zadań');
      onSaved(l);
    },
    onError: notifyError,
  });
  const remove = useMutation({ mutationFn: () => api<WorkDto[]>(url, { method: 'DELETE' }), onSuccess: (l) => (notifyOk(general ? 'Przywrócono listę wbudowaną' : 'Usunięto listę branży — wraca lista ogólna'), onSaved(l)), onError: notifyError });
  const set = (i: number, patch: Partial<ChecklistDef>) => setItems(items.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const dirty = JSON.stringify(items) !== JSON.stringify(work.items) || minPhotos !== work.minPhotos;
  const invalid = !items.length || items.some((i) => i.label.trim().length < 2);

  return (
    <Stack gap="sm">
      <Table.ScrollContainer minWidth={760}>
        <Table verticalSpacing={6}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={36} />
              <Table.Th>Czynność</Table.Th>
              <Table.Th w={90} ta="center">
                Wymagana
              </Table.Th>
              <Table.Th w={80} ta="center">
                Zdjęcie
              </Table.Th>
              <Table.Th w={210}>Wartość do wpisania</Table.Th>
              <Table.Th w={40} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((it, i) => (
              <Table.Tr key={i}>
                <Table.Td>
                  <Stack gap={0}>
                    <ActionIcon size="xs" variant="subtle" color="gray" disabled={i === 0} onClick={() => setItems(move(items, i, -1))} aria-label="W górę">
                      <IconArrowUp size={12} />
                    </ActionIcon>
                    <ActionIcon size="xs" variant="subtle" color="gray" disabled={i === items.length - 1} onClick={() => setItems(move(items, i, 1))} aria-label="W dół">
                      <IconArrowDown size={12} />
                    </ActionIcon>
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <TextInput size="xs" value={it.label} onChange={(e) => set(i, { label: e.currentTarget.value })} aria-label="Nazwa czynności" error={it.label.trim().length < 2 ? 'Podaj nazwę' : undefined} />
                </Table.Td>
                <Table.Td ta="center">
                  <Checkbox checked={it.required} onChange={(e) => set(i, { required: e.currentTarget.checked })} aria-label="Wymagana" style={{ display: 'inline-block' }} />
                </Table.Td>
                <Table.Td ta="center">
                  <Checkbox checked={it.requiresPhoto} onChange={(e) => set(i, { requiresPhoto: e.currentTarget.checked })} aria-label="Wymaga zdjęcia" style={{ display: 'inline-block' }} />
                </Table.Td>
                <Table.Td>
                  <Group gap={4} wrap="nowrap">
                    <Select
                      size="xs"
                      w={it.valueType === 'liczba' ? 120 : 190}
                      allowDeselect={false}
                      value={it.valueType ?? 'brak'}
                      data={[
                        { value: 'brak', label: 'bez wartości' },
                        { value: 'liczba', label: 'liczba' },
                        { value: 'tekst', label: 'tekst' },
                      ]}
                      onChange={(v) => set(i, { valueType: v === 'brak' ? null : (v as 'liczba' | 'tekst'), unit: v === 'liczba' ? it.unit : '' })}
                      aria-label="Wartość do wpisania"
                    />
                    {it.valueType === 'liczba' && <TextInput size="xs" w={70} placeholder="jedn." value={it.unit} maxLength={10} onChange={(e) => set(i, { unit: e.currentTarget.value })} aria-label="Jednostka" />}
                  </Group>
                </Table.Td>
                <Table.Td>
                  <ActionIcon variant="subtle" color="red" onClick={() => setItems(items.filter((_, j) => j !== i))} aria-label="Usuń czynność" disabled={items.length <= 1}>
                    <IconTrash size={14} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Group justify="space-between" wrap="wrap">
        <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} disabled={items.length >= 40} onClick={() => setItems([...items, { label: '', required: true, requiresPhoto: false, valueType: null, unit: '' }])}>
          Dodaj czynność
        </Button>
        <NumberInput size="xs" w={{ base: '100%', xs: 240 }} label="Minimalna liczba zdjęć w zadaniu" min={0} max={20} value={minPhotos} onChange={(v) => setMinPhotos(Number(v) || 0)} />
      </Group>
      <Group justify="space-between" wrap="wrap">
        <Text size="xs" c="dimmed">
          {isNew
            ? 'Branża korzysta z listy ogólnej — zapis utworzy jej własną listę.'
            : work.custom
              ? `Zmieniono: ${formatDateTime(work.updatedAt)}${work.updatedByName ? ` · ${work.updatedByName}` : ''}`
              : 'Lista wbudowana (jeszcze nie zmieniana)'}
        </Text>
        <Group gap="xs">
          {!isNew && (general ? work.custom : true) && (
            <Button
              variant="subtle"
              color={general ? 'gray' : 'red'}
              size="xs"
              loading={remove.isPending}
              onClick={async () => {
                const ok = await ask(general ? 'Zmiany w liście ogólnej zostaną zastąpione listą wbudowaną.' : 'Nowe zadania tej branży dostaną listę ogólną.', {
                  title: general ? 'Przywrócić listę wbudowaną?' : 'Usunąć listę branży?',
                  confirm: general ? 'Przywróć' : 'Usuń',
                  danger: !general,
                });
                if (ok) remove.mutate();
              }}
            >
              {general ? 'Przywróć wbudowaną' : 'Usuń listę branży'}
            </Button>
          )}
          <Button disabled={(!dirty && !isNew) || invalid} loading={save.isPending} onClick={() => save.mutate()}>
            Zapisz
          </Button>
        </Group>
      </Group>
    </Stack>
  );
}

// ── wzory protokołów ────────────────────────────────────────────────────────────

type FieldDraft = ProtocolTemplateDto['definition']['sections'][number]['fields'][number] & { optionsText?: string };
type Draft = { title: string; devices: boolean; declarations: string[]; sections: { title: string; fields: FieldDraft[] }[] };

const toDraft = (t: ProtocolTemplateDto): Draft => ({
  title: t.definition.title,
  devices: t.definition.devices,
  declarations: [...t.definition.declarations],
  sections: t.definition.sections.map((s) => ({ title: s.title, fields: s.fields.map((f) => ({ ...f, optionsText: (f.options ?? []).join(', ') })) })),
});
const fromDraft = (d: Draft) => ({
  title: d.title,
  devices: d.devices,
  declarations: d.declarations.map((x) => x.trim()).filter(Boolean),
  sections: d.sections.map((s) => ({
    title: s.title,
    fields: s.fields.map(({ optionsText, ...f }) => ({
      key: f.key,
      label: f.label,
      type: f.type,
      required: f.required,
      ...(f.type === 'liczba' && f.unit ? { unit: f.unit } : {}),
      ...(f.type === 'liczba' && f.min !== undefined ? { min: f.min } : {}),
      ...(f.type === 'liczba' && f.max !== undefined ? { max: f.max } : {}),
      ...(f.type === 'wybor' ? { options: (optionsText ?? '').split(',').map((o) => o.trim()).filter(Boolean) } : {}),
    })),
  })),
});
const FIELD_TYPES = [
  { value: 'tak_nie', label: 'tak / nie' },
  { value: 'liczba', label: 'liczba' },
  { value: 'tekst', label: 'tekst' },
  { value: 'wybor', label: 'wybór z listy' },
];

/** Edytor wzoru protokołu (w oknie — tytuł okna nazywa protokół i branżę). */
export function ProtocolEditor({
  tpl,
  onSaved,
  onDirtyChange,
}: {
  tpl: ProtocolTemplateDto;
  onSaved: (l: ProtocolTemplateDto[]) => void;
  /** zgłoszenie niezapisanych zmian — okno pyta przed zamknięciem */
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [d, setD] = useState<Draft>(toDraft(tpl));
  useEffect(() => setD(toDraft(tpl)), [tpl]);
  const save = useMutation({
    mutationFn: () => api<ProtocolTemplateDto[]>(`/settings/protocol-templates/${encodeURIComponent(tpl.code)}`, { method: 'PUT', body: fromDraft(d) }),
    onSuccess: (l) => {
      notifyOk(`Zapisano wersję ${tpl.version + 1} wzoru — dotyczy nowych protokołów`);
      onSaved(l);
    },
    onError: notifyError,
  });
  const dirty = JSON.stringify(fromDraft(d)) !== JSON.stringify(fromDraft(toDraft(tpl)));
  useEffect(() => onDirtyChange?.(dirty), [dirty]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => onDirtyChange?.(false), []); // eslint-disable-line react-hooks/exhaustive-deps
  const setSection = (i: number, patch: Partial<Draft['sections'][number]>) => setD({ ...d, sections: d.sections.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
  const setField = (si: number, fi: number, patch: Partial<FieldDraft>) => setSection(si, { fields: d.sections[si]!.fields.map((f, j) => (j === fi ? { ...f, ...patch } : f)) });
  const invalid = !d.title.trim() || d.sections.some((s) => s.title.trim().length < 2 || s.fields.some((f) => f.label.trim().length < 2 || (f.type === 'wybor' && (f.optionsText ?? '').split(',').filter((o) => o.trim()).length < 2)));

  const canSave = dirty && !invalid && !save.isPending;
  useSaveShortcut(canSave, () => save.mutate());

  return (
    <Stack gap="md">
      <Text size="xs" c="dimmed">
        Wersja {tpl.version} z {datePl(tpl.createdAt)}
        {tpl.signedCount ? ` · podpisane protokoły (${tpl.signedCount}) zachowują swoją wersję` : ''}
      </Text>
      <Group gap="md" align="flex-end" wrap="wrap">
        <TextInput label="Tytuł protokołu" value={d.title} onChange={(e) => setD({ ...d, title: e.currentTarget.value })} style={{ flex: '1 1 320px' }} />
        <Switch label="Lista urządzeń z numerami seryjnymi" checked={d.devices} onChange={(e) => setD({ ...d, devices: e.currentTarget.checked })} />
      </Group>

      {d.sections.map((s, si) => (
        <Card key={si} withBorder padding="sm">
          <Group justify="space-between" wrap="nowrap" mb="xs" gap="xs">
            <TextInput size="xs" label="Sekcja" value={s.title} onChange={(e) => setSection(si, { title: e.currentTarget.value })} style={{ flex: 1 }} />
            <Group gap={2} mt={18} wrap="nowrap">
              <ActionIcon variant="subtle" color="gray" disabled={si === 0} onClick={() => setD({ ...d, sections: move(d.sections, si, -1) })} aria-label="Sekcja w górę">
                <IconArrowUp size={14} />
              </ActionIcon>
              <ActionIcon variant="subtle" color="gray" disabled={si === d.sections.length - 1} onClick={() => setD({ ...d, sections: move(d.sections, si, 1) })} aria-label="Sekcja w dół">
                <IconArrowDown size={14} />
              </ActionIcon>
              <ActionIcon variant="subtle" color="red" disabled={d.sections.length <= 1} onClick={() => setD({ ...d, sections: d.sections.filter((_, j) => j !== si) })} aria-label="Usuń sekcję">
                <IconTrash size={14} />
              </ActionIcon>
            </Group>
          </Group>
          <Table.ScrollContainer minWidth={760}>
            <Table verticalSpacing={4}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={36} />
                  <Table.Th>Pole</Table.Th>
                  <Table.Th w={150}>Rodzaj</Table.Th>
                  <Table.Th w={190}>Jednostka / opcje</Table.Th>
                  <Table.Th w={90} ta="center">
                    Wymagane
                  </Table.Th>
                  <Table.Th w={40} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {s.fields.map((f, fi) => (
                  <Table.Tr key={fi}>
                    <Table.Td>
                      <Stack gap={0}>
                        <ActionIcon size="xs" variant="subtle" color="gray" disabled={fi === 0} onClick={() => setSection(si, { fields: move(s.fields, fi, -1) })} aria-label="W górę">
                          <IconArrowUp size={12} />
                        </ActionIcon>
                        <ActionIcon size="xs" variant="subtle" color="gray" disabled={fi === s.fields.length - 1} onClick={() => setSection(si, { fields: move(s.fields, fi, 1) })} aria-label="W dół">
                          <IconArrowDown size={12} />
                        </ActionIcon>
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <TextInput size="xs" value={f.label} onChange={(e) => setField(si, fi, { label: e.currentTarget.value })} aria-label="Nazwa pola" />
                    </Table.Td>
                    <Table.Td>
                      <Select size="xs" allowDeselect={false} value={f.type} data={FIELD_TYPES} onChange={(v) => setField(si, fi, { type: v as FieldDraft['type'] })} aria-label="Rodzaj pola" />
                    </Table.Td>
                    <Table.Td>
                      {f.type === 'liczba' && <TextInput size="xs" placeholder="jednostka, np. bar" value={f.unit ?? ''} maxLength={10} onChange={(e) => setField(si, fi, { unit: e.currentTarget.value })} aria-label="Jednostka" />}
                      {f.type === 'wybor' && (
                        <TextInput size="xs" placeholder="opcje po przecinku" value={f.optionsText ?? ''} onChange={(e) => setField(si, fi, { optionsText: e.currentTarget.value })} aria-label="Opcje wyboru" error={(f.optionsText ?? '').split(',').filter((o) => o.trim()).length < 2 ? 'min. 2 opcje' : undefined} />
                      )}
                    </Table.Td>
                    <Table.Td ta="center">
                      <Checkbox checked={f.required} onChange={(e) => setField(si, fi, { required: e.currentTarget.checked })} aria-label="Wymagane" style={{ display: 'inline-block' }} />
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon variant="subtle" color="red" onClick={() => setSection(si, { fields: s.fields.filter((_, j) => j !== fi) })} aria-label="Usuń pole">
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
          <Button size="compact-xs" variant="subtle" leftSection={<IconPlus size={12} />} mt={4} onClick={() => setSection(si, { fields: [...s.fields, { key: '', label: '', type: 'tak_nie', required: true }] })}>
            Dodaj pole
          </Button>
        </Card>
      ))}
      <Button size="xs" variant="light" leftSection={<IconPlus size={14} />} w="fit-content" onClick={() => setD({ ...d, sections: [...d.sections, { title: 'Nowa sekcja', fields: [{ key: '', label: '', type: 'tak_nie', required: true }] }] })}>
        Dodaj sekcję
      </Button>

      <div>
        <Text size="sm" fw={600} mb={4}>
          Oświadczenia nad podpisem klienta
        </Text>
        <Stack gap={6}>
          {d.declarations.map((x, i) => (
            <Group key={i} gap="xs" wrap="nowrap" align="flex-start">
              <Textarea size="xs" autosize minRows={1} value={x} onChange={(e) => setD({ ...d, declarations: d.declarations.map((y, j) => (j === i ? e.currentTarget.value : y)) })} style={{ flex: 1 }} aria-label="Oświadczenie" />
              <ActionIcon variant="subtle" color="red" onClick={() => setD({ ...d, declarations: d.declarations.filter((_, j) => j !== i) })} aria-label="Usuń oświadczenie">
                <IconTrash size={14} />
              </ActionIcon>
            </Group>
          ))}
          <Button size="compact-xs" variant="subtle" leftSection={<IconPlus size={12} />} w="fit-content" disabled={d.declarations.length >= 10} onClick={() => setD({ ...d, declarations: [...d.declarations, ''] })}>
            Dodaj oświadczenie
          </Button>
        </Stack>
      </div>
      <Group justify="flex-end">
        <Button disabled={!dirty || invalid} loading={save.isPending} onClick={() => save.mutate()}>
          Zapisz jako wersję {tpl.version + 1}
        </Button>
      </Group>
    </Stack>
  );
}
